import {
  ClipboardCheck, KeyRound, Pencil, Plus, Trash2, UserCheck, UserX, Wand2,
} from 'lucide-react';
import { useCallback, useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { del, get, post, put, toQuery } from '../api';
import { useAsk } from '../components/Ask';
import { ErrorBox, Modal, Spinner, StatusBadge } from '../components/base';
import { moduleAllowsProduct } from '../modules';
import { ModuleConfigModal } from '../components/ModuleConfig';
import { ReadinessPanel } from '../components/Readiness';
import { CATEGORY_LABELS, STATUS_LABELS, formatDate } from '../formats';
import type {
  Client, DocumentFull, DocumentSummary, ModuleDefinition, PortalUser, Product,
} from '../types';

/**
 * Owner console: clients, logins, products, metric imports and documents.
 * One screen with tabs rather than five pages - this is back office for one
 * person, not a product surface.
 */

const TABS = ['Clients', 'Users', 'Products', 'Metrics', 'Documents'] as const;
type Tab = typeof TABS[number];

export default function Admin() {
  const [tab, setTab] = useState<Tab>('Clients');
  const [clients, setClients] = useState<Client[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [reloadKey, setReloadKey] = useState(0);
  const reload = useCallback(() => setReloadKey((k) => k + 1), []);

  useEffect(() => {
    get<{ clients: Client[] }>('/clients').then(({ clients: c }) => setClients(c)).catch(() => {});
    get<{ products: Product[] }>('/products').then(({ products: p }) => setProducts(p)).catch(() => {});
  }, [reloadKey]);

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-semibold">Admin</h1>
        <div className="flex flex-wrap rounded-lg border border-paper-300 dark:border-ink-700" role="tablist">
          {TABS.map((t) => (
            <button
              key={t}
              type="button"
              role="tab"
              aria-selected={tab === t}
              onClick={() => setTab(t)}
              className={`px-3.5 py-2 text-sm font-medium first:rounded-l-lg last:rounded-r-lg ${
                tab === t
                  ? 'bg-ink-900 text-paper-100 dark:bg-paper-100 dark:text-ink-900'
                  : 'text-ink-600 hover:bg-paper-100 dark:text-paper-300 dark:hover:bg-ink-700'
              }`}
            >
              {t}
            </button>
          ))}
        </div>
      </div>

      {tab === 'Clients' && <ClientsTab clients={clients} reload={reload} />}
      {tab === 'Users' && <UsersTab clients={clients} reloadKey={reloadKey} />}
      {tab === 'Products' && <ProductsTab clients={clients} products={products} reload={reload} />}
      {tab === 'Metrics' && <MetricsTab products={products} />}
      {tab === 'Documents' && <DocumentsTab clients={clients} products={products} />}
    </div>
  );
}

// -- shared shell -------------------------------------------------------------

function Table({ head, children }: { head: string[]; children: ReactNode }) {
  return (
    <div className="card overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-paper-200 text-left dark:border-ink-700">
            {head.map((h) => (
              <th key={h} className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-ink-500 dark:text-ink-400">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-paper-200 dark:divide-ink-700">{children}</tbody>
      </table>
    </div>
  );
}

function useSubmit(action: () => Promise<void>) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const run = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await action();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Something went wrong.');
    } finally {
      setBusy(false);
    }
  };
  return { busy, error, run };
}

// -- clients ------------------------------------------------------------------

function ClientsTab({ clients, reload }: { clients: Client[]; reload: () => void }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ slug: '', name: '', contact_email: '' });
  const { busy, error, run } = useSubmit(async () => {
    await post('/clients', form);
    setOpen(false);
    setForm({ slug: '', name: '', contact_email: '' });
    reload();
  });

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <button type="button" className="btn-primary" onClick={() => setOpen(true)}>
          <Plus className="h-4 w-4" aria-hidden /> New client
        </button>
      </div>
      <Table head={['Name', 'Slug', 'Products', 'Logins', 'Active']}>
        {clients.map((c) => (
          <tr key={c.id}>
            {/* The row used to be inert: the only way from a client to its
                products was the Dashboard's selector. The name now opens the
                Dashboard filtered to this client, which is where its products
                and their Manage links live. */}
            <td className="px-4 py-3 font-medium">
              <Link to={`/?client=${c.id}`} className="hover:underline">{c.name}</Link>
            </td>
            <td className="px-4 py-3 font-mono text-xs">{c.slug}</td>
            <td className="px-4 py-3">{c.n_products ?? 0}</td>
            <td className="px-4 py-3">{c.n_users ?? 0}</td>
            <td className="px-4 py-3">{c.active ? 'Yes' : 'No'}</td>
          </tr>
        ))}
      </Table>

      <Modal open={open} onClose={() => setOpen(false)} title="New client" width="max-w-md">
        <form onSubmit={run} className="space-y-4">
          {error && <ErrorBox message={error} />}
          <div>
            <label className="form-label" htmlFor="c-name">Name</label>
            <input id="c-name" className="field" required value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          <div>
            <label className="form-label" htmlFor="c-slug">Slug (lowercase, hyphens)</label>
            <input id="c-slug" className="field" required value={form.slug}
              placeholder="peak-pro"
              onChange={(e) => setForm({ ...form, slug: e.target.value })} />
          </div>
          <div>
            <label className="form-label" htmlFor="c-email">Contact email (optional)</label>
            <input id="c-email" type="email" className="field" value={form.contact_email}
              onChange={(e) => setForm({ ...form, contact_email: e.target.value })} />
          </div>
          <button type="submit" className="btn-primary" disabled={busy}>
            {busy && <Spinner className="h-4 w-4" />} Create
          </button>
        </form>
      </Modal>
    </div>
  );
}

// -- users --------------------------------------------------------------------

function UsersTab({ clients, reloadKey }: { clients: Client[]; reloadKey: number }) {
  const { confirm } = useAsk();
  const [users, setUsers] = useState<PortalUser[]>([]);
  const [open, setOpen] = useState(false);
  const [localReload, setLocalReload] = useState(0);
  const [form, setForm] = useState({
    name: '', email: '', password: '', role: 'client', client_id: '',
  });
  const [resetting, setResetting] = useState<PortalUser | null>(null);
  const [resetPassword, setResetPassword] = useState('');
  // The API requires the acting owner's own password before it will set
  // anybody else's, so an open session on a borrowed laptop is not enough.
  const [ownPassword, setOwnPassword] = useState('');
  const [resetDone, setResetDone] = useState(false);

  useEffect(() => {
    get<{ users: PortalUser[] }>('/auth/users').then(({ users: u }) => setUsers(u)).catch(() => {});
  }, [reloadKey, localReload]);

  const { busy, error, run } = useSubmit(async () => {
    await post('/auth/users', {
      ...form,
      client_id: form.role === 'client' ? form.client_id : undefined,
    });
    setOpen(false);
    setForm({ name: '', email: '', password: '', role: 'client', client_id: '' });
    setLocalReload((k) => k + 1);
  });

  async function suggest() {
    const { password } = await get<{ password: string }>('/auth/suggest-password');
    setForm((f) => ({ ...f, password }));
  }

  const reset = useSubmit(async () => {
    if (!resetting) return;
    await put(`/auth/users/${resetting.id}/password`, {
      password: resetPassword, current_password: ownPassword,
    });
    setResetDone(true);
  });

  async function suggestReset() {
    const { password } = await get<{ password: string }>('/auth/suggest-password');
    setResetPassword(password);
  }

  function openReset(user: PortalUser) {
    setResetting(user);
    setResetPassword('');
    setOwnPassword('');
    setResetDone(false);
  }

  /**
   * The off-switch. Deactivating cuts the account off on its next request, not
   * when its token expires - so this, and a password reset, are the two ways to
   * end somebody's access without rotating the platform's session secret.
   */
  async function setActive(user: PortalUser, active: boolean) {
    if (!active && !await confirm({
      title: `Deactivate ${user.name}?`,
      message: `${user.email} is signed out immediately and cannot sign in again until reactivated.`,
      confirmLabel: 'Deactivate',
      danger: true,
    })) return;
    await put(`/auth/users/${user.id}`, { active });
    setLocalReload((k) => k + 1);
  }

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <button type="button" className="btn-primary" onClick={() => setOpen(true)}>
          <Plus className="h-4 w-4" aria-hidden /> New user
        </button>
      </div>
      <Table head={['Name', 'Email', 'Role', 'Client', 'Last sign-in', 'Active', '']}>
        {users.map((u) => (
          <tr key={u.id}>
            <td className="px-4 py-3 font-medium">{u.name}</td>
            <td className="px-4 py-3">{u.email}</td>
            <td className="px-4 py-3 capitalize">{u.role}</td>
            <td className="px-4 py-3">{u.client_name ?? '-'}</td>
            <td className="px-4 py-3">{u.last_login ? formatDate(u.last_login) : 'Never'}</td>
            <td className="px-4 py-3">{u.active ? 'Yes' : 'No'}</td>
            <td className="px-4 py-3 text-right">
              <button
                type="button"
                className="rounded-lg p-1.5 text-ink-400 hover:bg-paper-200 hover:text-ink-700 dark:hover:bg-ink-700"
                aria-label={`Reset password for ${u.name}`}
                title="Reset password"
                onClick={() => openReset(u)}
              >
                <KeyRound className="h-4 w-4" aria-hidden />
              </button>
              <button
                type="button"
                className="rounded-lg p-1.5 text-ink-400 hover:bg-paper-200 hover:text-ink-700 dark:hover:bg-ink-700"
                aria-label={`${u.active ? 'Deactivate' : 'Reactivate'} ${u.name}`}
                title={u.active ? 'Deactivate this login' : 'Reactivate this login'}
                onClick={() => setActive(u, !u.active)}
              >
                {u.active
                  ? <UserX className="h-4 w-4" aria-hidden />
                  : <UserCheck className="h-4 w-4" aria-hidden />}
              </button>
            </td>
          </tr>
        ))}
      </Table>

      <Modal
        open={resetting !== null}
        onClose={() => setResetting(null)}
        title={`Reset password - ${resetting?.name ?? ''}`}
        width="max-w-md"
      >
        {resetDone ? (
          <div className="space-y-4">
            <p
              role="status"
              className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800
                         dark:border-emerald-900/60 dark:bg-emerald-950/40 dark:text-emerald-200"
            >
              Password changed for {resetting?.email}. Share it through a safe channel
              (not email together with the address); they can change it under Account.
            </p>
            <button type="button" className="btn-secondary" onClick={() => setResetting(null)}>
              Close
            </button>
          </div>
        ) : (
          <form onSubmit={reset.run} className="space-y-4">
            {reset.error && <ErrorBox message={reset.error} />}
            <div>
              <label className="form-label" htmlFor="r-password">New password (minimum 12 characters)</label>
              <div className="flex gap-2">
                <input
                  id="r-password" className="field font-mono" required minLength={12}
                  value={resetPassword}
                  onChange={(e) => setResetPassword(e.target.value)}
                />
                <button type="button" className="btn-secondary shrink-0" onClick={suggestReset} title="Suggest a strong password">
                  <Wand2 className="h-4 w-4" aria-hidden />
                </button>
              </div>
            </div>
            <div>
              <label className="form-label" htmlFor="r-own">Your own password</label>
              <input
                id="r-own" type="password" className="field" required
                autoComplete="current-password"
                value={ownPassword}
                onChange={(e) => setOwnPassword(e.target.value)}
              />
              <p className="mt-1 text-xs text-ink-500 dark:text-ink-400">
                Confirms it is you setting someone else's password, not whoever
                found this screen open.
              </p>
            </div>
            <button type="submit" className="btn-primary" disabled={reset.busy}>
              {reset.busy && <Spinner className="h-4 w-4" />} Set password
            </button>
          </form>
        )}
      </Modal>

      <Modal open={open} onClose={() => setOpen(false)} title="New user" width="max-w-md">
        <form onSubmit={run} className="space-y-4">
          {error && <ErrorBox message={error} />}
          <div>
            <label className="form-label" htmlFor="u-name">Name</label>
            <input id="u-name" className="field" required value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          <div>
            <label className="form-label" htmlFor="u-email">Email</label>
            <input id="u-email" type="email" className="field" required value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </div>
          <div>
            <label className="form-label" htmlFor="u-role">Role</label>
            <select id="u-role" className="field" value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value })}>
              <option value="client">Client</option>
              <option value="owner">Owner (snip.work staff)</option>
            </select>
          </div>
          {form.role === 'client' && (
            <div>
              <label className="form-label" htmlFor="u-client">Client</label>
              <select id="u-client" className="field" required value={form.client_id}
                onChange={(e) => setForm({ ...form, client_id: e.target.value })}>
                <option value="">Choose…</option>
                {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
          )}
          <div>
            <label className="form-label" htmlFor="u-password">Password (minimum 12 characters)</label>
            <div className="flex gap-2">
              <input id="u-password" className="field font-mono" required minLength={12}
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })} />
              <button type="button" className="btn-secondary shrink-0" onClick={suggest} title="Suggest a strong password">
                <Wand2 className="h-4 w-4" aria-hidden />
              </button>
            </div>
            <p className="mt-1 text-xs text-ink-500 dark:text-ink-400">
              Share it with the client through a safe channel; they can change it afterwards.
            </p>
          </div>
          <button type="submit" className="btn-primary" disabled={busy}>
            {busy && <Spinner className="h-4 w-4" />} Create
          </button>
        </form>
      </Modal>
    </div>
  );
}

// -- products -----------------------------------------------------------------

const EMPTY_PRODUCT = {
  id: '', client_id: '', slug: '', name: '', type: 'website', status: 'in_development',
  url: '', origin_url: '', launched_at: '', description: '', stack: '', public_key: '',
  cf_site_tag: '', outcome_metric: '', modules: [] as string[],
};

/**
 * The Inbox module's ingest key for a product, read on demand and never stored
 * in the browser beyond this modal. The product's backend sends it as a bearer
 * token with the product's public key in the body - both are shown here so the
 * whole call can be copied in one go.
 */
function InboxKey({ productId, publicKey }: { productId: string; publicKey: string }) {
  const [data, setData] = useState<{ key: string; endpoint: string } | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    get<{ key: string; endpoint: string }>(`/products/${productId}/inbox-key`)
      .then(setData)
      .catch((e) => setError(e instanceof Error ? e.message : 'Could not read the key.'));
  }, [productId]);

  const snippet = data
    ? `POST ${data.endpoint}\nAuthorization: Bearer ${data.key}\n`
      + `{ "key": "${publicKey}", "form": "register", "page": "/register", "fields": { ... } }`
    : '';

  return (
    <div>
      <span className="form-label">Inbox ingest (the product's backend posts each submission)</span>
      {error && <p className="text-xs text-red-700 dark:text-red-300">{error}</p>}
      {data && (
        <div className="flex items-start gap-2">
          <code className="block flex-1 overflow-x-auto whitespace-pre rounded-lg bg-paper-200 p-3 font-mono text-[11px] dark:bg-ink-700">
            {snippet}
          </code>
          <button
            type="button" className="btn-secondary shrink-0"
            onClick={() => navigator.clipboard.writeText(data.key)}
          >
            Copy key
          </button>
        </div>
      )}
      <p className="mt-1 text-xs text-ink-500 dark:text-ink-400">
        The key is derived from the platform secret and this product; it is a Worker secret on the
        product's side, never in its repository. Rotating INBOX_KEY_SECRET rotates every product's key.
      </p>
    </div>
  );
}

/** The analytics embed for a product - one tag, added before </body>. */
function embedSnippet(publicKey: string): string {
  return `<script defer src="${window.location.origin}/api/beacon.js" data-site="${publicKey}"></script>`;
}

function ProductsTab({ clients, products, reload }: {
  clients: Client[]; products: Product[]; reload: () => void;
}) {
  const [editing, setEditing] = useState<typeof EMPTY_PRODUCT | null>(null);
  const [registry, setRegistry] = useState<ModuleDefinition[]>([]);
  const [checking, setChecking] = useState<{ id: string; name: string } | null>(null);
  const [configuring, setConfiguring] = useState<string | null>(null);
  const isNew = editing !== null && editing.id === '';

  useEffect(() => {
    get<{ modules: ModuleDefinition[] }>('/modules/registry')
      .then(({ modules: m }) => setRegistry(m)).catch(() => {});
  }, []);

  const { busy, error, run } = useSubmit(async () => {
    if (!editing) return;
    if (isNew) {
      // A new product is checked immediately: the standard is only real if
      // the gaps are visible the moment the product exists.
      const { product } = await post<{ product: { id: string; name: string } }>(
        '/products', editing);
      setChecking(product);
    } else {
      await put(`/products/${editing.id}`, editing);
      // Module toggles are their own endpoint - apply any changes.
      const before = products.find((p) => p.id === editing.id)?.modules ?? [];
      for (const definition of registry.filter((d) => moduleAllowsProduct(d, editing.slug))) {
        const was = before.includes(definition.key);
        const wants = editing.modules.includes(definition.key);
        if (was !== wants) {
          await put(`/products/${editing.id}/modules`, { module: definition.key, enabled: wants });
        }
      }
    }
    setEditing(null);
    reload();
  });

  const field = (key: keyof typeof EMPTY_PRODUCT) => ({
    value: editing?.[key] ?? '',
    onChange: (e: { target: { value: string } }) =>
      setEditing((p) => (p ? { ...p, [key]: e.target.value } : p)),
  });

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <button type="button" className="btn-primary" onClick={() => setEditing({ ...EMPTY_PRODUCT })}>
          <Plus className="h-4 w-4" aria-hidden /> New product
        </button>
      </div>
      <Table head={['Name', 'Client', 'Status', 'URL', '']}>
        {products.map((p) => (
          <tr key={p.id}>
            <td className="px-4 py-3 font-medium">{p.name}</td>
            <td className="px-4 py-3">{p.client_name}</td>
            <td className="px-4 py-3">
              <StatusBadge status={p.status} label={STATUS_LABELS[p.status] ?? p.status} />
            </td>
            <td className="max-w-[220px] truncate px-4 py-3 text-xs">{p.url ?? '-'}</td>
            <td className="px-4 py-3 text-right">
              <button
                type="button"
                className="rounded-lg p-1.5 text-ink-400 hover:bg-paper-200 hover:text-ink-700 dark:hover:bg-ink-700"
                aria-label={`Check readiness of ${p.name}`}
                title="Launch readiness"
                onClick={() => setChecking({ id: p.id, name: p.name })}
              >
                <ClipboardCheck className="h-4 w-4" aria-hidden />
              </button>
              <button
                type="button"
                className="rounded-lg p-1.5 text-ink-400 hover:bg-paper-200 hover:text-ink-700 dark:hover:bg-ink-700"
                aria-label={`Edit ${p.name}`}
                onClick={() => setEditing({
                  id: p.id, client_id: p.client_id, slug: p.slug, name: p.name,
                  type: p.type, status: p.status, url: p.url ?? '',
                  origin_url: p.origin_url ?? '',
                  launched_at: p.launched_at ?? '', description: p.description ?? '',
                  stack: p.stack ?? '', public_key: p.public_key ?? '',
                  cf_site_tag: p.cf_site_tag ?? '',
                  outcome_metric: p.outcome_metric ?? '',
                  modules: p.modules ?? [],
                })}
              >
                <Pencil className="h-4 w-4" aria-hidden />
              </button>
            </td>
          </tr>
        ))}
      </Table>

      <Modal
        open={checking !== null}
        onClose={() => setChecking(null)}
        title={`Launch readiness - ${checking?.name ?? ''}`}
      >
        {checking && <ReadinessPanel productId={checking.id} />}
      </Modal>

      <Modal
        open={editing !== null}
        onClose={() => setEditing(null)}
        title={isNew ? 'New product' : 'Edit product'}
      >
        <form onSubmit={run} className="space-y-4">
          {error && <ErrorBox message={error} />}
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="form-label" htmlFor="p-name">Name</label>
              <input id="p-name" className="field" required {...field('name')} />
            </div>
            {isNew && (
              <>
                <div>
                  <label className="form-label" htmlFor="p-client">Client</label>
                  <select id="p-client" className="field" required {...field('client_id')}>
                    <option value="">Choose…</option>
                    {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="form-label" htmlFor="p-slug">Slug</label>
                  <input id="p-slug" className="field" required {...field('slug')} placeholder="main-site" />
                </div>
                <div>
                  <label className="form-label" htmlFor="p-type">Type</label>
                  <select id="p-type" className="field" {...field('type')}>
                    <option value="website">Website</option>
                    <option value="app">App</option>
                    <option value="service">Service</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </>
            )}
            <div>
              <label className="form-label" htmlFor="p-status">Status</label>
              <select id="p-status" className="field" {...field('status')}>
                {Object.entries(STATUS_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="form-label" htmlFor="p-url">URL</label>
              <input id="p-url" className="field" {...field('url')} placeholder="https://…" />
            </div>
            <div>
              <label className="form-label" htmlFor="p-origin">Origin address (checks only)</label>
              <input
                id="p-origin" className="field" {...field('origin_url')}
                placeholder="https://project.pages.dev — only for snip.work-hosted products"
              />
              <p className="mt-1 text-xs text-ink-500 dark:text-ink-400">
                The App cannot read snip.work hosts through its own routes. Readiness and
                uptime read this address instead when set.
              </p>
            </div>
            <div>
              <label className="form-label" htmlFor="p-launched">Launched (YYYY-MM-DD)</label>
              <input id="p-launched" className="field" {...field('launched_at')} placeholder="2026-01-31" />
            </div>
            <div>
              <label className="form-label" htmlFor="p-stack">Built with</label>
              <input id="p-stack" className="field" {...field('stack')} />
            </div>
            <div>
              <label className="form-label" htmlFor="p-outcome">
                Outcome metric (the number this client is paying for)
              </label>
              <input
                id="p-outcome" className="field font-mono" placeholder="leads, orders, signups…"
                {...field('outcome_metric')}
              />
              <p className="mt-1 text-xs text-ink-500 dark:text-ink-400">
                Level 2 of the product standard. The product's backend posts these to /api/events.
              </p>
            </div>
            <div>
              <label className="form-label" htmlFor="p-cftag">Cloudflare Web Analytics site tag</label>
              <input
                id="p-cftag" className="field font-mono" placeholder="32 hex characters, optional"
                {...field('cf_site_tag')}
              />
              <p className="mt-1 text-xs text-ink-500 dark:text-ink-400">
                Set = traffic comes from Cloudflare; the portal beacon is ignored for this product.
              </p>
            </div>
          </div>
          <div>
            <label className="form-label" htmlFor="p-desc">Description (shown to the client)</label>
            <textarea id="p-desc" className="field min-h-[80px]" {...field('description')} />
          </div>
          {!isNew && editing && registry.some((d) => moduleAllowsProduct(d, editing.slug)) && (
            <div>
              <span className="form-label">Modules (what this client can manage in the App)</span>
              <div className="space-y-2">
                {registry.filter((d) => moduleAllowsProduct(d, editing.slug)).map((definition) => (
                  <div key={definition.key} className="flex items-start gap-2 text-sm">
                    <label className="flex flex-1 items-start gap-2">
                      <input
                        type="checkbox"
                        className="mt-0.5"
                        checked={editing.modules.includes(definition.key)}
                        onChange={(e) => setEditing((p) => (p ? {
                          ...p,
                          modules: e.target.checked
                            ? [...p.modules, definition.key]
                            : p.modules.filter((k) => k !== definition.key),
                        } : p))}
                      />
                      <span>
                        <span className="font-medium">{definition.label}</span>
                        <span className="block text-xs text-ink-500 dark:text-ink-400">
                          {definition.description}
                        </span>
                      </span>
                    </label>
                    {/* Only for a module that has settings, and only once it is
                        switched on - configuring something that is off reads as
                        if it were running. Its own modal because the settings
                        save through their own endpoint, validated server-side,
                        and must not ride along with the product form. */}
                    {definition.tenantAgnostic && definition.configurable !== false
                      && editing.modules.includes(definition.key) && (
                      <button
                        type="button"
                        className="btn-secondary shrink-0 text-xs"
                        onClick={() => setConfiguring(definition.key)}
                      >
                        Settings
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
          {!isNew && editing?.public_key && editing.modules.includes('inbox') && (
            <InboxKey productId={editing.id} publicKey={editing.public_key} />
          )}
          {!isNew && editing?.public_key && (
            <div>
              <span className="form-label">Analytics embed (add before &lt;/body&gt; on the product's site)</span>
              <div className="flex items-start gap-2">
                <code className="block flex-1 overflow-x-auto rounded-lg bg-paper-200 p-3 font-mono text-[11px] dark:bg-ink-700">
                  {embedSnippet(editing.public_key)}
                </code>
                <button
                  type="button" className="btn-secondary shrink-0"
                  onClick={() => navigator.clipboard.writeText(embedSnippet(editing.public_key))}
                >
                  Copy
                </button>
              </div>
              <p className="mt-1 text-xs text-ink-500 dark:text-ink-400">
                Pageviews, unique visitors and visits appear on the product page within 15 minutes.
                No cookies, no personal data stored.
              </p>
            </div>
          )}
          <button type="submit" className="btn-primary" disabled={busy}>
            {busy && <Spinner className="h-4 w-4" />} {isNew ? 'Create' : 'Save'}
          </button>
        </form>
      </Modal>

      {configuring && editing && (
        <ModuleConfigModal
          productId={editing.id}
          moduleKey={configuring}
          onClose={() => setConfiguring(null)}
          onSaved={reload}
        />
      )}
    </div>
  );
}

// -- metrics ------------------------------------------------------------------

function MetricsTab({ products }: { products: Product[] }) {
  const [productId, setProductId] = useState('');
  const [source, setSource] = useState('');
  const [raw, setRaw] = useState('');
  const [result, setResult] = useState<string | null>(null);

  const { busy, error, run } = useSubmit(async () => {
    setResult(null);
    const rows = raw.split('\n').map((l) => l.trim()).filter(Boolean).map((line, i) => {
      const [date, metric, value, lineSource] = line.split(',').map((p) => p.trim());
      if (!date || !metric || value === undefined) {
        throw new Error(`Line ${i + 1}: expected "date,metric,value" - got "${line}".`);
      }
      const parsed = Number(value);
      if (!Number.isFinite(parsed)) throw new Error(`Line ${i + 1}: "${value}" is not a number.`);
      return { date, metric, value: parsed, source: lineSource || source || undefined };
    });
    const { imported } = await post<{ imported: number }>(`/products/${productId}/metrics`, { rows });
    setResult(`Imported ${imported} row(s). Existing dates were overwritten, not duplicated.`);
    setRaw('');
  });

  return (
    <form onSubmit={run} className="card max-w-2xl space-y-4 p-5">
      <div>
        <h2 className="font-semibold">Import metrics</h2>
        <p className="mt-1 text-sm text-ink-500 dark:text-ink-400">
          One row per line: <code className="font-mono text-xs">date,metric,value[,source]</code> -
          for example <code className="font-mono text-xs">2026-08-01,visits,412,cloudflare-analytics</code>.
          Every number needs a named source.
        </p>
      </div>
      {error && <ErrorBox message={error} />}
      {result && (
        <p role="status" className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800 dark:border-emerald-900/60 dark:bg-emerald-950/40 dark:text-emerald-200">
          {result}
        </p>
      )}
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label className="form-label" htmlFor="m-product">Product</label>
          <select id="m-product" className="field" required value={productId}
            onChange={(e) => setProductId(e.target.value)}>
            <option value="">Choose…</option>
            {products.map((p) => (
              <option key={p.id} value={p.id}>{p.client_name} - {p.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="form-label" htmlFor="m-source">Default source</label>
          <input id="m-source" className="field" value={source} placeholder="cloudflare-analytics"
            onChange={(e) => setSource(e.target.value)} />
        </div>
      </div>
      <div>
        <label className="form-label" htmlFor="m-rows">Rows</label>
        <textarea id="m-rows" className="field min-h-[160px] font-mono text-xs" required value={raw}
          placeholder={'2026-08-01,visits,412\n2026-08-01,unique_visitors,301'}
          onChange={(e) => setRaw(e.target.value)} />
      </div>
      <button type="submit" className="btn-primary" disabled={busy}>
        {busy && <Spinner className="h-4 w-4" />} Import
      </button>
    </form>
  );
}

// -- documents ----------------------------------------------------------------

const EMPTY_DOC = {
  id: '', client_id: '', product_id: '', category: 'update', title: '', body: '', pinned: false,
};

function DocumentsTab({ clients, products }: { clients: Client[]; products: Product[] }) {
  const { confirm } = useAsk();
  const [documents, setDocuments] = useState<DocumentSummary[]>([]);
  const [editing, setEditing] = useState<typeof EMPTY_DOC | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const isNew = editing !== null && editing.id === '';

  useEffect(() => {
    get<{ documents: DocumentSummary[] }>('/documents')
      .then(({ documents: d }) => setDocuments(d)).catch(() => {});
  }, [reloadKey]);

  const { busy, error, run } = useSubmit(async () => {
    if (!editing) return;
    const payload = {
      client_id: editing.client_id,
      product_id: editing.product_id || null,
      category: editing.category,
      title: editing.title,
      body: editing.body,
      pinned: editing.pinned,
    };
    if (isNew) {
      await post('/documents', payload);
    } else {
      await put(`/documents/${editing.id}`, payload);
    }
    setEditing(null);
    setReloadKey((k) => k + 1);
  });

  async function edit(id: string) {
    const { document } = await get<{ document: DocumentFull }>(`/documents/${id}`);
    setEditing({
      id: document.id, client_id: document.client_id, product_id: document.product_id ?? '',
      category: document.category, title: document.title, body: document.body,
      pinned: document.pinned === 1,
    });
  }

  async function remove(id: string, title: string) {
    // The API snapshots the body into the audit log, so this is recoverable.
    if (!await confirm({
      title: `Delete "${title}"?`,
      message: 'The client stops seeing it at once. The text is kept in the audit trail.',
      confirmLabel: 'Delete',
      danger: true,
    })) return;
    await del(`/documents/${id}`);
    setReloadKey((k) => k + 1);
  }

  const clientProducts = products.filter((p) => p.client_id === editing?.client_id);

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <button type="button" className="btn-primary" onClick={() => setEditing({ ...EMPTY_DOC })}>
          <Plus className="h-4 w-4" aria-hidden /> New document
        </button>
      </div>
      <Table head={['Title', 'Client', 'Category', 'Product', 'Updated', '']}>
        {documents.map((d) => (
          <tr key={d.id}>
            <td className="px-4 py-3 font-medium">{d.title}{d.pinned === 1 ? ' *' : ''}</td>
            <td className="px-4 py-3">{d.client_name}</td>
            <td className="px-4 py-3">{CATEGORY_LABELS[d.category]}</td>
            <td className="px-4 py-3">{d.product_name ?? '-'}</td>
            <td className="px-4 py-3">{formatDate(d.updated_at)}</td>
            <td className="px-4 py-3 text-right">
              <button
                type="button"
                className="rounded-lg p-1.5 text-ink-400 hover:bg-paper-200 hover:text-ink-700 dark:hover:bg-ink-700"
                aria-label={`Edit ${d.title}`}
                onClick={() => edit(d.id)}
              >
                <Pencil className="h-4 w-4" aria-hidden />
              </button>
              <button
                type="button"
                className="rounded-lg p-1.5 text-ink-400 hover:bg-red-100 hover:text-red-700 dark:hover:bg-red-950/50"
                aria-label={`Delete ${d.title}`}
                onClick={() => remove(d.id, d.title)}
              >
                <Trash2 className="h-4 w-4" aria-hidden />
              </button>
            </td>
          </tr>
        ))}
      </Table>

      <Modal
        open={editing !== null}
        onClose={() => setEditing(null)}
        title={isNew ? 'New document' : 'Edit document'}
        width="max-w-3xl"
      >
        <form onSubmit={run} className="space-y-4">
          {error && <ErrorBox message={error} />}
          <div className="grid gap-4 sm:grid-cols-3">
            <div>
              <label className="form-label" htmlFor="d-client">Client</label>
              <select
                id="d-client" className="field" required disabled={!isNew}
                value={editing?.client_id ?? ''}
                onChange={(e) => setEditing((d) => (d ? { ...d, client_id: e.target.value, product_id: '' } : d))}
              >
                <option value="">Choose…</option>
                {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label className="form-label" htmlFor="d-product">Product (optional)</label>
              <select
                id="d-product" className="field" disabled={!isNew}
                value={editing?.product_id ?? ''}
                onChange={(e) => setEditing((d) => (d ? { ...d, product_id: e.target.value } : d))}
              >
                <option value="">Whole engagement</option>
                {clientProducts.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            <div>
              <label className="form-label" htmlFor="d-category">Category</label>
              <select
                id="d-category" className="field"
                value={editing?.category ?? 'update'}
                onChange={(e) => setEditing((d) => (d ? { ...d, category: e.target.value } : d))}
              >
                {Object.entries(CATEGORY_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className="form-label" htmlFor="d-title">Title</label>
            <input
              id="d-title" className="field" required
              value={editing?.title ?? ''}
              onChange={(e) => setEditing((d) => (d ? { ...d, title: e.target.value } : d))}
            />
          </div>
          <div>
            <label className="form-label" htmlFor="d-body">Body (markdown: headings, lists, tables, links)</label>
            <textarea
              id="d-body" className="field min-h-[220px] font-mono text-xs" required
              value={editing?.body ?? ''}
              onChange={(e) => setEditing((d) => (d ? { ...d, body: e.target.value } : d))}
            />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={editing?.pinned ?? false}
              onChange={(e) => setEditing((d) => (d ? { ...d, pinned: e.target.checked } : d))}
            />
            Pin to the top of the client's list
          </label>
          <button type="submit" className="btn-primary" disabled={busy}>
            {busy && <Spinner className="h-4 w-4" />} {isNew ? 'Publish' : 'Save'}
          </button>
        </form>
      </Modal>
    </div>
  );
}
